import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight } from 'lucide-react';
import { caseStudies } from '../mockData';

export const CaseStudies = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Case Studies - StatZen Analytics</title>
        <link rel="canonical" href="https://statzenanalytics.com/case-studies" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Case Studies
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Explore detailed analyses from fertility research projects demonstrating 
              rigorous statistical methods and actionable insights
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study) => (
              <Card
                key={study.id}
                className="overflow-hidden hover:shadow-xl transition-shadow duration-300 border-gray-200 group flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-blue-600 text-white text-xs px-3 py-1">
                      {study.category}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 mb-4 flex-1 line-clamp-3">
                    {study.description}
                  </p>
                  <Link to={`/case-studies/${study.slug}`} className="mt-auto">
                    <button className="flex items-center space-x-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-300">
                      <span>View Details</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Research Areas */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Research Areas
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Specialized expertise across multiple domains in fertility and healthcare research
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'IVF Success Rate Analysis',
              'Embryo Quality Assessment',
              'Treatment Outcome Studies',
              'Predictive Modeling for Fertility',
              'Ovarian Stimulation Protocols',
              'Clinical Trial Design & Analysis',
              'Quality Improvement Studies',
              'Data Quality Audits',
              'Performance Benchmarking'
            ].map((area, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-200"
              >
                <h3 className="font-semibold text-gray-900">{area}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Start Your Research Project
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Ready to apply rigorous statistical methods to your fertility research?
          </p>
          <Link to="/contact">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300">
              Get Started Today
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
